import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatPercent,
  type FinancingCommissionSettings,
  type FinancingCommissionSettingsBody,
} from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Card, Skeleton, useToast } from '../../ds/components';
import { ApiError, api, errorMessage } from '../../lib/api';
import { SettingsPageHeader } from './SettingsFrame';
import frameStyles from './SettingsFrame.module.css';
import styles from './FinancingCommissionSettingsPage.module.css';

/**
 * Einstellungen 29 „Provision für Finanzierungen" (`GET/PUT /settings/commission-financing`, admins only): the
 * default rate per financier and the share per person. „Speichern" sends every row; the live sum turns red above
 * 100 % and blocks the save with the server's text. Unsaved changes ask before the tab closes.
 */

const settingsKey = ['settings', 'commission-financing'] as const;
const SUM_TOO_HIGH = 'Die Summe der Anteile darf 100 % nicht übersteigen.';
const RATE_RANGE = 'Der Satz muss zwischen 0 und 100 % liegen.';
const SHARE_RANGE = 'Der Anteil muss zwischen 0 und 100 % liegen.';

/** 0.8 → "0,80", 40 → "40" (the field shows the % sign itself). */
function percentText(value: number): string {
  return formatPercent(value, 2).replace(/\s%$/u, '');
}

/** "0,80" / "0.8" / "40 %" → the number; "" → null; anything else → 'invalid'. */
function parsePercent(text: string): number | null | 'invalid' {
  const trimmed = text.replace('%', '').trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(value) || value < 0 || value > 100) return 'invalid';
  if (Math.abs(Math.round(value * 1000) - value * 1000) > 1e-6) return 'invalid';
  return value;
}

function PercentField({
  label,
  value,
  invalid,
  onChange,
}: {
  label: string;
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <span className={clsx(styles.percent, invalid && styles.percentInvalid)}>
      <input
        className={styles.percentInput}
        aria-label={label}
        aria-invalid={invalid || undefined}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <span aria-hidden="true">%</span>
    </span>
  );
}

function Person({ name, sub }: { name: string; sub: string }) {
  return (
    <div className={styles.person}>
      <span className={styles.avatar} aria-hidden="true" />
      <div className={styles.personText}>
        <span className={styles.personName}>{name}</span>
        <span className={styles.personSub}>{sub}</span>
      </div>
    </div>
  );
}

export function FinancingCommissionSettingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: ({ signal }) => api<FinancingCommissionSettings>('/settings/commission-financing', { signal }),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
  const data = settings.data;
  const [rates, setRates] = useState<Record<string, string>>({});
  const [shares, setShares] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<{
    financiers?: string | undefined;
    shares?: string | undefined;
  }>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    setRates(Object.fromEntries(data.financiers.map((row) => [row.userId, percentText(row.ratePct)])));
    setShares(Object.fromEntries(data.shares.map((row) => [row.userId, percentText(row.sharePct)])));
  }, [data]);

  const parsedRates = useMemo(
    () => (data?.financiers ?? []).map((row) => ({ row, value: parsePercent(rates[row.userId] ?? '') })),
    [data, rates],
  );
  const parsedShares = useMemo(
    () => (data?.shares ?? []).map((row) => ({ row, value: parsePercent(shares[row.userId] ?? '') })),
    [data, shares],
  );
  const sumPct = parsedShares.reduce(
    (sum, entry) => sum + (typeof entry.value === 'number' ? entry.value : 0),
    0,
  );
  const sumTooHigh = sumPct > 100 + 1e-9;
  const dirty =
    parsedRates.some((entry) => entry.value !== entry.row.ratePct) ||
    parsedShares.some((entry) => entry.value !== entry.row.sharePct);

  useEffect(() => {
    if (!dirty) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  const invalidRate = parsedRates.some((entry) => typeof entry.value !== 'number');
  const invalidShare = parsedShares.some((entry) => typeof entry.value !== 'number');

  async function save() {
    if (!data || saving || sumTooHigh || invalidRate || invalidShare) return;
    const body: FinancingCommissionSettingsBody = {
      financiers: parsedRates.map((entry) => ({ userId: entry.row.userId, ratePct: Number(entry.value) })),
      shares: parsedShares.map((entry) => ({ userId: entry.row.userId, sharePct: Number(entry.value) })),
    };
    setSaving(true);
    setBanner(null);
    setServerErrors({});
    try {
      const next = await api<FinancingCommissionSettings>('/settings/commission-financing', {
        method: 'PUT',
        body,
      });
      queryClient.setQueryData(settingsKey, next);
      toast({ message: 'Gespeichert.' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0) {
        setServerErrors({ financiers: error.fields['financiers'], shares: error.fields['shares'] });
      } else setBanner(errorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  const forbidden = settings.error instanceof ApiError && settings.error.status === 403;

  return (
    <div className={frameStyles.page}>
      <SettingsPageHeader
        eyebrow="Organisation"
        title={data?.title ?? 'Provision für Finanzierungen'}
        description={
          data?.intro ??
          'Die Provision zahlt der Finanzierer an den Vertrieb. Sie wird bei „Ausgezahlt“ fällig und nach den Anteilen unten verteilt.'
        }
        actions={
          forbidden ? null : (
            <Button
              loading={saving}
              disabled={!data || sumTooHigh || invalidRate || invalidShare}
              onClick={() => void save()}
            >
              Speichern
            </Button>
          )
        }
      />
      {settings.isError ? <Banner tone="danger">{errorMessage(settings.error)}</Banner> : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      {settings.isPending ? (
        <>
          <Card className={styles.skeletonCard}>
            <Skeleton width="30%" height={16} />
            <Skeleton width="70%" />
            <Skeleton variant="block" height={44} />
            <Skeleton variant="block" height={44} />
          </Card>
          <Card className={styles.skeletonCard}>
            <Skeleton width="30%" height={16} />
            <Skeleton variant="block" height={44} />
            <Skeleton variant="block" height={44} />
            <Skeleton variant="block" height={44} />
          </Card>
        </>
      ) : null}
      {data ? (
        <>
          <section className={styles.card} aria-labelledby="financing-rates-title">
            <div className={styles.cardHead}>
              <h3 id="financing-rates-title" className={styles.cardTitle}>
                {data.ratesTitle}
              </h3>
              <p className={styles.cardText}>{data.ratesText}</p>
            </div>
            <div className={clsx(styles.row, styles.rates, styles.rowHead)} aria-hidden="true">
              <span>Finanzierer</span>
              <span>Standard-Satz</span>
              <span>Zuletzt geändert</span>
              <span />
            </div>
            {data.financiers.length === 0 ? (
              <div className={styles.empty}>Noch kein aktiver Finanzierer.</div>
            ) : null}
            {parsedRates.map(({ row, value }) => (
              <div key={row.userId} className={clsx(styles.row, styles.rates)}>
                <Person name={row.name} sub={row.email} />
                <div className={styles.field}>
                  <PercentField
                    label={`Standard-Satz ${row.name}`}
                    value={rates[row.userId] ?? ''}
                    invalid={typeof value !== 'number'}
                    onChange={(next) => setRates({ ...rates, [row.userId]: next })}
                  />
                  {typeof value !== 'number' ? <span className={styles.fieldError}>{RATE_RANGE}</span> : null}
                </div>
                <span className={styles.muted}>{row.attribution}</span>
                <span />
              </div>
            ))}
            {serverErrors.financiers ? (
              <div className={styles.empty} role="alert">
                <span className={styles.fieldError}>{serverErrors.financiers}</span>
              </div>
            ) : null}
            <div className={styles.note}>{data.notes.rates}</div>
          </section>

          <section className={styles.card} aria-labelledby="financing-shares-title">
            <div className={styles.cardHead}>
              <h3 id="financing-shares-title" className={styles.cardTitle}>
                {data.sharesTitle}
              </h3>
              <p className={styles.cardText}>{data.sharesText}</p>
            </div>
            <div className={clsx(styles.row, styles.shares, styles.rowHead)} aria-hidden="true">
              <span>Person</span>
              <span>Rolle</span>
              <span>Anteil</span>
              <span />
            </div>
            {parsedShares.map(({ row, value }) => (
              <div key={row.userId} className={clsx(styles.row, styles.shares)}>
                <Person name={row.name} sub={row.roleLabel} />
                <span className={styles.muted}>{row.roleLabel}</span>
                <div className={styles.field}>
                  <PercentField
                    label={`Anteil ${row.name}`}
                    value={shares[row.userId] ?? ''}
                    invalid={typeof value !== 'number'}
                    onChange={(next) => setShares({ ...shares, [row.userId]: next })}
                  />
                  {typeof value !== 'number' ? (
                    <span className={styles.fieldError}>{SHARE_RANGE}</span>
                  ) : null}
                </div>
                <span />
              </div>
            ))}
            <div className={clsx(styles.row, styles.shares, styles.sumRow, sumTooHigh && styles.sumOver)}>
              <span className={styles.sumLabel}>Summe</span>
              <span />
              <span className={styles.sumValue}>{formatPercent(sumPct, 2)}</span>
              <span
                className={styles.sumMessage}
                role={sumTooHigh || serverErrors.shares ? 'alert' : undefined}
              >
                {sumTooHigh ? SUM_TOO_HIGH : (serverErrors.shares ?? '')}
              </span>
            </div>
            <div className={styles.note}>{data.notes.shares}</div>
          </section>
        </>
      ) : null}
    </div>
  );
}
